import * as React from 'react';

import { Label } from '@ui/components/ui/label';
import { cn } from '@ui/lib/utils';
import { latestClockTime } from '@ui/lib/time';

export type TimeFieldProps = {
  id?: string;
  label: React.ReactNode;
  /** `HH:mm`, or empty while the field does not hold a whole time yet. */
  value: string;
  onChange: (next: string) => void;
  /** What is in the boxes: `value` is no use mid-typing, where a half-typed minute pads into a time nobody gave. */
  onTyping?: (state: { partial: boolean; latest?: string }) => void;
  /** Shown under the field once it has been left holding something that is not a time. */
  invalidMessage: string;
  hourPlaceholder?: string;
  minutePlaceholder?: string;
  hourLabel?: string;
  minuteLabel?: string;
  /** Marks the label and both boxes as an answer the form will not go without. */
  required?: boolean;
  className?: string;
};

/** Two digits, so `9` is written back as `09` and the two boxes stay the same width. */
const pad = (part: string): string => (part === '' ? '' : part.padStart(2, '0'));

const isHour = (part: string): boolean => part !== '' && Number(part) <= 23;
const isMinute = (part: string): boolean => part !== '' && Number(part) <= 59;

/** A time of day, as two boxes with a colon between them. */
export function TimeField({
  id,
  label,
  value,
  onChange,
  onTyping,
  invalidMessage,
  hourPlaceholder = '22',
  minutePlaceholder = '30',
  hourLabel = 'Hours',
  minuteLabel = 'Minutes',
  required,
  className,
}: TimeFieldProps) {
  const genId = React.useId();
  const fieldId = id ?? genId;
  const minuteRef = React.useRef<HTMLInputElement>(null);

  const [hour, setHour] = React.useState(() => value.split(':')[0] ?? '');
  const [minute, setMinute] = React.useState(() => value.split(':')[1] ?? '');
  const [visited, setVisited] = React.useState(false);
  const [isEditing, setIsEditing] = React.useState(false);

  // What the boxes last agreed on, so an outside reset reaches them but a half-typed time does not wipe itself.
  const emitted = React.useRef(value);
  React.useEffect(() => {
    if (value === emitted.current) return;
    emitted.current = value;
    setHour(value.split(':')[0] ?? '');
    setMinute(value.split(':')[1] ?? '');
  }, [value]);

  const emit = (nextHour: string, nextMinute: string): void => {
    const whole = isHour(nextHour) && isMinute(nextMinute) ? `${pad(nextHour)}:${pad(nextMinute)}` : '';
    emitted.current = whole;
    onChange(whole);
  };

  // A digit above 2 cannot start a two-digit hour, so waiting for a second would eat the minutes.
  const handleHour = (typed: string): void => {
    const [digits, afterSeparator] = typed.split(/[:.,hH]/);
    const entered = digits.replace(/\D/g, '').slice(0, 2);
    const nextHour = entered.length === 1 && Number(entered) > 2 ? pad(entered) : entered;
    setHour(nextHour);
    if (afterSeparator !== undefined || nextHour.length === 2) {
      if (afterSeparator) {
        const nextMinute = afterSeparator.replace(/\D/g, '').slice(0, 2);
        setMinute(nextMinute);
        emit(nextHour, nextMinute);
      } else {
        emit(nextHour, minute);
      }
      minuteRef.current?.focus();
      return;
    }
    emit(nextHour, minute);
  };

  const handleMinute = (typed: string): void => {
    const nextMinute = typed.replace(/\D/g, '').slice(0, 2);
    setMinute(nextMinute);
    emit(hour, nextMinute);
  };

  const handleBlur = (event: React.FocusEvent<HTMLDivElement>): void => {
    // Moving between the two boxes is not leaving the field, and must not tidy it half way through.
    if (event.currentTarget.contains(event.relatedTarget)) return;
    setIsEditing(false);
    setVisited(true);
    const tidyHour = pad(hour);
    // An hour on its own means that hour exactly, which is how "I started at nine" is written down.
    const tidyMinute = minute === '' && tidyHour !== '' ? '00' : pad(minute);
    setHour(tidyHour);
    setMinute(tidyMinute);
    emit(tidyHour, tidyMinute);
  };

  const isFilled = hour !== '' || minute !== '';
  // Only while another digit could still arrive, which is the one thing a padded value hides.
  const isPartial = isEditing && (minute.length < 2 || hour.length < 2);
  const latest = latestClockTime(hour, minute);
  React.useEffect(() => {
    onTyping?.({ partial: isPartial, latest });
  }, [isPartial, latest, onTyping]);

  const isInvalid = visited && !isEditing && isFilled && !(isHour(hour) && isMinute(minute === '' ? '00' : minute));

  // The example sits further back than muted text does: at full muted weight it reads as a filled field.
  const box = 'w-7 bg-transparent text-center tabular-nums outline-none placeholder:text-muted-foreground/50';

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Label htmlFor={fieldId} required={required}>
        {label}
      </Label>
      <div
        data-slot="time-field"
        onBlur={handleBlur}
        onFocus={() => setIsEditing(true)}
        aria-invalid={isInvalid}
        className={cn(
          'flex h-8 w-fit items-center gap-0.5 rounded-lg border border-input bg-transparent px-2.5 py-1 text-base transition-colors md:text-sm dark:bg-input/30',
          'focus-within:border-ring focus-within:ring-3 focus-within:ring-ring/50',
          isInvalid && 'border-destructive ring-3 ring-destructive/20',
        )}
      >
        <input
          id={fieldId}
          aria-label={hourLabel}
          aria-required={required}
          inputMode="numeric"
          autoComplete="off"
          placeholder={hourPlaceholder}
          className={box}
          value={hour}
          onChange={event => handleHour(event.target.value)}
        />
        <span aria-hidden className="text-muted-foreground">
          :
        </span>
        <input
          ref={minuteRef}
          id={`${fieldId}-minutes`}
          aria-label={minuteLabel}
          aria-required={required}
          inputMode="numeric"
          autoComplete="off"
          placeholder={minutePlaceholder}
          className={box}
          value={minute}
          onChange={event => handleMinute(event.target.value)}
          // Backspace on an empty minute box goes back to the hours, where the caret would go anyway.
          onKeyDown={event => {
            if (event.key === 'Backspace' && minute === '') {
              event.preventDefault();
              document.getElementById(fieldId)?.focus();
            }
          }}
        />
      </div>
      {isInvalid ? <p className="text-xs text-destructive">{invalidMessage}</p> : null}
    </div>
  );
}
